/*       */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { muiTheme } from 'app/themes/materialUi';
import { Link } from 'react-router-dom';
import { Divider, Box, Typography } from '@mic3/platform-ui';

import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import WidgetHeader from 'app/components/atoms/WidgetHeader/WidgetHeader';
import Loader from 'app/components/atoms/Loader/Loader';
import Title from 'app/components/atoms/Title/Title';
import ButtonIcon from 'app/components/molecules/ButtonIcon/ButtonIcon';
import { ChildrenProp } from 'app/utils/propTypes/common';
import ScrollBarMin from 'app/utils/styles/ScrollMinStyle';
import { canViewWidget, refreshExceptions, widgetTypes } from 'app/containers/LandingPage/dashboardUtils';
import { useSelector } from 'react-redux';

export const WidgetBase = styled.div`
    position: relative;
    border-radius: 0.2rem;
    min-height: 360px;
    height: 100%;
    background: ${({ theme }) => theme.material.colors.background.paper};
    box-shadow: ${({ theme }) => theme.shadow.boxShadow2DP};
`;
const WidgetFooter = styled.div`
    padding: 3px 8px 8px 8px;
    width: 100%;
    background: ${({ theme }) => theme.material.colors.background.paper};
    & i:before {
        color: ${({ theme }) => theme.material.colors.link};
    }
    & a {
        text-decoration: none;
    }
`;

const Content = styled.div`
    font-size: 0.8rem;
    overflow: auto;
    height: ${({ heightOffset, hideFooter }) => `calc(100% - ${hideFooter ? 10 : heightOffset || 70}px)`};
    &::-webkit-scrollbar {
        width: 4px !important;
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.material.colors.background.scrollbar};
    }
    color: ${({ theme }) => theme.base.textColor} !important;
    ${({ $noResult }) => $noResult && 'padding: 12px 20px;'};
    ${(props) => props}
`;

const Children = styled.div`
    ${ScrollBarMin};
`;

const SubTitle = styled(Title)`
    margin-left: 0.5rem !important;
    font-size: 0.7rem !important;
    color: ${({ theme }) => theme.base.textColor || 'inherit'} !important;
    opacity: 0.5;
`;
const StyledLink = styled(Link)`
    text-transform: uppercase;
    text-align: center;
`;

const StyledDivider = styled(Divider)`
    width: auto !important;
    margin: 0px 16px 16px 16px !important;
`;
const HeaderContainer = styled('div')`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: 3.2rem;
    cursor: ${({ isDragable }) => (isDragable ? 'grab' : 'default')};
`;
const StyledTitle = styled(Title)`
    color: ${({ theme }) => theme.material.colors.text.caption};
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: auto;
    text-align: right;
    letter-spacing: 0.4px;
`;
const Flex = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: -webkit-fill-available;
    ${(props) => props}
`;

const StyledHeading = styled(Title)`
    font-weight: 500;
    font-size: 18px !important;
    line-height: 28px !important;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: auto;
`;

const Widget = ({
    title,
    subTitle,
    content,
    children,
    loading,
    url,
    urlText,
    renderAppAvatar,
    footerContent,
    subHeader,
    menuItemClick,
    hideFooter = false,
    hideDivider = false,
    contentProps = {},
    permissions,
    type,
    item,
    ...rest
}) => {
    const widgetRef = useRef(null);
    const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

    const canView = canViewWidget(item, type);

    useEffect(() => {
        if (!widgetRef.current) return;

        const parentElement = widgetRef.current?.parentElement;
        if (!parentElement) return;

        const updateHeight = () => {
            if (widgetRef.current) {
                widgetRef.current.style.height = `${parentElement.clientHeight}px`;
            }
        };
        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(parentElement);

        return () => resizeObserver.disconnect();
    }, []);

    const { canEdit } = permissions || {};

    return (
        <WidgetBase ref={widgetRef} {...rest}>
            {loading && <Loader absolute />}
            <Flex style={{ height: '100%' }}>
                <div style={{ position: 'absolute', top: '17px', right: '5px' }}>
                    <DotMenu
                        onItemClick={menuItemClick}
                        icon='cog'
                        padded
                        items={[
                            !refreshExceptions.includes(type) && canView && { name: 'Refresh', icon: 'refresh' },
                            !refreshExceptions.includes(type) && canView && canEdit && { name: 'divider' },
                            canEdit && type !== widgetTypes.alive && { name: 'Edit widget', icon: 'pencil' },
                            // { name: 'Duplicate widget', icon: 'content-copy' },
                            canEdit && { name: 'Remove widget', icon: 'delete' },
                        ].filter(Boolean)}
                    />
                </div>
                <div style={{ height: '-webkit-fill-available', overflow: 'hidden' }}>
                    <HeaderContainer isDragable={!isSidebarOpen && canEdit} className='drag-handle'>
                        <WidgetHeader>
                            {renderAppAvatar && renderAppAvatar()}
                            <StyledHeading
                                as='h3'
                                style={{
                                    color: muiTheme.colors.text.landingPage,
                                }}
                            >
                                {title || 'No Name'}
                            </StyledHeading>
                            <SubTitle forwardedAs='h4'>{subTitle}</SubTitle>
                        </WidgetHeader>
                        {subHeader && <StyledTitle as='h4'>{subHeader}</StyledTitle>}
                    </HeaderContainer>
                    {canView ? (
                        <>
                            {content && (
                                <Content {...contentProps} $noResult={typeof content === 'string' && content.includes('No ')}>
                                    {content}
                                </Content>
                            )}
                            {children && <Children>{children}</Children>}
                        </>
                    ) : (
                        <Box>
                            <Typography variant='body1' align='center' style={{ color: 'rgba(255, 255, 255, 0.38)', marginTop: '2rem' }}>
                                You do not have permission to view this widget.
                            </Typography>
                        </Box>
                    )}
                </div>
                {!hideFooter && canView && (
                    <WidgetFooter>
                        {!hideDivider && <StyledDivider />}
                        {url && !footerContent && (
                            <StyledLink to={url}>
                                <ButtonIcon icon='arrow-right' size='sm' />
                                {urlText || 'VIEW ALL'}
                            </StyledLink>
                        )}
                        {!!footerContent && footerContent()}
                    </WidgetFooter>
                )}
            </Flex>
        </WidgetBase>
    );
};

Widget.propTypes = {
    title: PropTypes.string,
    subTitle: PropTypes.string,
    content: ChildrenProp,
    loading: PropTypes.bool,
    url: PropTypes.string,
    urlText: PropTypes.string,
};

export default Widget;
